import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const EASE = [0.22, 1, 0.36, 1];

export const KineticLines = ({ lines, delay = 0.15, lineClassName = "" }) => (
  <>
    {lines.map((line, i) => (
      <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
        <motion.span
          className={`block ${lineClassName}`}
          initial={{ y: "115%" }}
          animate={{ y: "0%" }}
          transition={{ duration: 0.9, delay: delay + i * 0.12, ease: EASE }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </>
);

export const Reveal = ({ children, delay = 0, y = 32, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

export const Overline = ({ children, light = false, className = "" }) => (
  <p className={`overline-mono ${light ? "text-white/60" : "text-forest"} ${className}`}>
    {children}
  </p>
);

export const SectionHeading = ({ overline, title, text, light = false }) => (
  <div className="max-w-3xl">
    <Reveal y={16}>
      <Overline light={light}>{overline}</Overline>
    </Reveal>
    <Reveal delay={0.1}>
      <h2
        className={`mt-5 font-display font-light text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] ${
          light ? "text-white" : "text-graphite"
        }`}
      >
        {title}
      </h2>
    </Reveal>
    {text && (
      <Reveal delay={0.2}>
        <p className={`mt-6 text-base md:text-lg leading-relaxed ${light ? "text-white/70" : "text-graphite-soft"}`}>
          {text}
        </p>
      </Reveal>
    )}
  </div>
);

export const PageHero = ({ overline, titleLines, intro, testid }) => (
  <header className="border-b border-graphite/10 bg-bone structural-grid">
    <div className="mx-auto max-w-7xl px-6 pt-40 pb-20 md:pt-52 md:pb-28">
      {overline && (
        <Reveal y={16}>
          <Overline>{overline}</Overline>
        </Reveal>
      )}
      <h1
        className="mt-6 font-display font-light text-5xl md:text-6xl lg:text-[5rem] tracking-tighter leading-[1.05] text-graphite"
        data-testid={`${testid}-title`}
      >
        <KineticLines lines={titleLines} />
      </h1>
      {intro && (
        <Reveal delay={0.45}>
          <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-graphite-soft">{intro}</p>
        </Reveal>
      )}
    </div>
  </header>
);

export const StatusBadge = ({ status }) => {
  const { t } = useLanguage();
  const colors = {
    done: "bg-forest",
    progress: "bg-science",
    prep: "bg-graphite-soft",
    vision: "bg-graphite/30",
  };
  return (
    <span
      className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-graphite-soft"
      data-testid={`status-badge-${status}`}
    >
      <span className={`h-1.5 w-1.5 ${colors[status] || "bg-graphite"}`} />
      {t(`status.${status}`)}
    </span>
  );
};

export const MarqueeBand = ({ items }) => (
  <div className="border-y border-graphite/10 bg-white py-5" data-testid="editorial-marquee">
    <Marquee speed={28} gradient={false}>
      {items.map((it, i) => (
        <span
          key={i}
          className="mx-10 inline-flex items-center gap-10 font-mono text-xs uppercase tracking-[0.25em] text-graphite-soft"
        >
          {it}
          <span className="text-forest">+</span>
        </span>
      ))}
    </Marquee>
  </div>
);

export const CTAButton = ({ to, children, variant = "primary", testid }) => {
  const base =
    "group inline-flex items-center gap-3 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300";
  const styles = {
    primary: "bg-forest text-white hover:bg-graphite",
    light: "bg-white text-graphite hover:bg-forest hover:text-white",
    outline: "border border-graphite/30 text-graphite hover:border-forest hover:text-forest",
    outlineLight: "border border-white/40 text-white hover:border-white hover:bg-white/10",
  };
  return (
    <Link to={to} data-testid={testid} className={`${base} ${styles[variant]}`}>
      {children}
      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
    </Link>
  );
};
